import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';

/**
 * SiteContext — global CMS content loaded from /api/site at boot.
 * Exposes:
 *   - settings: dictionary (contacts, colors, socials, orari, logo urls, ecc.)
 *   - texts:    dictionary (page-scoped text keys)
 *   - menu:     ordered array of menu items
 *   - seo:      per-page seo dict
 *   - t(key, fallback):   read a text with fallback
 *   - s(key, fallback):   read a setting with fallback
 *   - reload():           re-fetch from server
 *
 * Also injects CSS variables from settings.color_* into <html> style so any
 * palette change from the admin is applied live sitewide.
 */

const DEFAULTS = { settings: {}, texts: {}, menu: [], seo: {} };
const SiteContext = createContext({ ...DEFAULTS, loading: true, reload: () => { }, t: (k, f) => f, s: (k, f) => f });

function hexToHsl(hex) {
    if (!hex || typeof hex !== 'string') return null;
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6) return null;
    const r = parseInt(h.substring(0, 2), 16) / 255;
    const g = parseInt(h.substring(2, 4), 16) / 255;
    const b = parseInt(h.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let hue = 0, sat = 0; const light = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
            case g: hue = (b - r) / d + 2; break;
            case b: hue = (r - g) / d + 4; break;
            default: hue = 0;
        }
        hue *= 60;
    }
    return `${Math.round(hue)} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`;
}

function applyThemeVars(settings) {
    if (typeof document === 'undefined') return;
    const map = {
        '--primary': settings.color_primary,
        '--ring': settings.color_primary,
        '--atelier-sage-ink': settings.color_primary,
        '--secondary': settings.color_secondary,
        '--atelier-ivory': settings.color_secondary,
        '--atelier-cream': settings.color_secondary,
        '--background': settings.color_background,
        '--foreground': settings.color_text,
        '--muted-foreground': settings.color_muted,
        '--border': settings.color_border,
        '--input': settings.color_border,
        '--accent': settings.color_accent,
        '--atelier-bronze': settings.color_accent,
        '--card': settings.color_card,
    };
    const root = document.documentElement;
    Object.entries(map).forEach(([varName, hex]) => {
        const hsl = hexToHsl(hex);
        if (hsl) root.style.setProperty(varName, hsl);
    });
}

export const SiteProvider = ({ children }) => {
    const [state, setState] = useState({ ...DEFAULTS, loading: true });

    const load = useCallback(async () => {
        try {
            const res = await api.get('/site');
            const data = res.data || {};
            setState({
                settings: data.settings || {},
                texts: data.texts || {},
                menu: data.menu || [],
                seo: data.seo || {},
                loading: false,
            });
            if (data.settings) applyThemeVars(data.settings);
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[SiteContext] Failed to load site config:', err);
            }
            setState((s) => ({ ...s, loading: false }));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const t = useCallback((key, fallback = '') => state.texts?.[key] ?? fallback, [state.texts]);
    const s = useCallback((key, fallback = '') => state.settings?.[key] ?? fallback, [state.settings]);

    const value = useMemo(() => ({ ...state, reload: load, t, s }), [state, load, t, s]);

    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);

// Compute WhatsApp URL from settings, with fallback
export const useWhatsappUrl = () => {
    const { settings } = useSite();
    if (settings.whatsapp_url) return settings.whatsapp_url;
    if (settings.whatsapp_number) return `https://wa.me/${String(settings.whatsapp_number).replace(/\D/g, '')}`;
    return 'https://wa.me/390296779924';
};
